import React, { useId } from 'react';
import { IoArrowForward } from 'react-icons/io5';
import { COUNTRIES } from '../../config/countries';
import type { CountryCode } from '../../config/countries';
import { PRODUCT_LABELS } from '../../config/products';
import type { DataProductId } from '../../config/products';
import { isChoosable } from '../../hooks/useCoverage';
import type { CoverageStatus } from '../../hooks/useCoverage';
import { formatDayLabel } from '../../utils/dateWindow';
import { joinNames } from '../../utils/plural';
import DeliveryDayBars from './DeliveryDayBars';

const ArrowIcon = IoArrowForward as any;

interface CountryCoverageRowProps {
  country: CountryCode;
  product: DataProductId;
  status: CoverageStatus;
  /** Chooser only: when given and isChoosable(status), the country name is a button that calls it. */
  onChoose?: (c: CountryCode) => void;
  /** Shown as "Try again" for unknown + retryable. */
  onRetry?: () => void;
  /** A small pill after the country name (Status marks its rows "Demo pipeline"). Nothing shows without it. */
  tag?: string;
}

function statusLine(status: CoverageStatus): string {
  switch (status.kind) {
    case 'delivered':
      return `Latest: ${formatDayLabel(status.latest)}`;
    case 'checking':
      return 'Checking deliveries…';
    case 'none':
      return 'No days delivered to your account yet.';
    case 'no-access':
      return 'Your account has no access to this data.';
    case 'unknown':
      return "Delivery days couldn't be checked.";
    case 'not-offered': {
      const where = status.alternatives.map((c) => COUNTRIES[c].label);
      return where.length > 0 ? `${status.reason} Available in: ${joinNames(where)}.` : status.reason;
    }
    case 'demo':
      return 'Sample data from the demo service.';
  }
}

/** One country's delivery status for one product: its name, a line of status, and the delivery-day bars. */
const CountryCoverageRow: React.FC<CountryCoverageRowProps> = ({ country, product, status, onChoose, onRetry, tag }) => {
  const nameId = useId();
  const statusId = useId();
  const { label, flag } = COUNTRIES[country];
  const choosable = isChoosable(status);
  const asButton = choosable && onChoose !== undefined;
  const bars = status.kind === 'delivered' || (status.kind === 'demo' && status.dates.length > 0) ? status.dates : null;

  const name = (
    <>
      <span aria-hidden="true">{flag}</span>
      <span>{label}</span>
      {asButton ? <ArrowIcon aria-hidden="true" className="h-4 w-4" /> : null}
    </>
  );

  return (
    <li aria-labelledby={nameId} className="grid gap-2 p-4 sm:grid-cols-[13rem_minmax(0,1fr)] sm:items-center">
      <div>
        {asButton ? (
          <button
            type="button"
            id={nameId}
            aria-describedby={statusId}
            onClick={() => onChoose(country)}
            className="inline-flex items-center gap-2 rounded-lg text-left font-semibold text-ink hover:text-brand-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500"
          >
            {name}
          </button>
        ) : (
          <span
            id={nameId}
            className={`inline-flex items-center gap-2 font-semibold ${
              choosable || !onChoose ? 'text-ink' : 'text-ink-subtle'
            }`}
          >
            {name}
          </span>
        )}
        {tag ? (
          <span className="ml-2 rounded-full bg-paper-200 px-2 py-0.5 text-[11px] font-mono uppercase tracking-widest text-ink-subtle">
            {tag}
          </span>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <p id={statusId} className="text-sm text-ink-muted">{statusLine(status)}</p>
          {status.kind === 'unknown' && status.retryable && onRetry ? (
            <button type="button" className="btn btn-ghost btn-xs" onClick={onRetry}>
              Try again
            </button>
          ) : null}
        </div>
      </div>
      <div>
        {bars ? (
          <DeliveryDayBars mode="view" dates={bars} label={`${PRODUCT_LABELS[product]} in ${label}, delivery days`} />
        ) : status.kind === 'checking' ? (
          <div aria-hidden="true" className="h-8 animate-pulse rounded bg-paper-200" />
        ) : null}
      </div>
    </li>
  );
};

export default CountryCoverageRow;
